import { useCallback, useEffect, useState } from "react";

import AddressFormatter from "@shopify/address";
import map from "lodash/map";
import reduce from "lodash/reduce";
import moment from "moment";
import { FormattedMessage } from "react-intl";
import { useDispatch, useSelector } from "react-redux";

import { Divider } from "@ps-design-system/components/Divider";
import { PaginationControls } from "@ps-design-system/components/Pagination";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@ps-design-system/components/Select";
import { Spinner } from "@ps-design-system/components/Spinner";
import { Text } from "@ps-design-system/components/Text";

import SharedIntlProviderHOC from "shared-components/hocs/sharedIntlProviderHOC";

import { useIntl } from "react-intl-compat";

import { fetchInvoicesByYear } from "../../../app-main/actions/invoices";
import { selectAuthAccessToken } from "../../../app-main/selectors/auth.selectors";
import {
  selectProfileIsHost,
  selectProfileSSOid,
} from "../../../app-main/selectors/profile.selectors";

import { InvoiceCard } from "./InvoiceCard";
import {
  FIRST_PAGE,
  INVOICES_PER_PAGE,
  PDF_CONTAINER_ID,
} from "./TaxInvoices.consts";
import {
  selectInvoiceCount,
  selectInvoices,
  selectInvoicesAreFetching,
  selectInvoiceYearsSorted,
  selectLatestInvoiceYear,
} from "./TaxInvoices.selectors";
import type { Country, Invoice } from "./TaxInvoices.types";
import { useInvoiceDownloads } from "./useInvoiceDownloads";
import { usePagination } from "./usePagination";

/**
 * Renders the guest's tax invoices: a year picker, a page of invoice cards and
 * the controls to page through them.
 */
const TaxInvoicesList = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const locale = SharedIntlProviderHOC.getLocale();

  const invoices = useSelector(selectInvoices);
  const invoiceYears = useSelector(selectInvoiceYearsSorted);
  const latestYear = useSelector(selectLatestInvoiceYear);
  const invoiceCount = useSelector(selectInvoiceCount);
  const areInvoicesFetching = useSelector(selectInvoicesAreFetching);
  const isHost = useSelector(selectProfileIsHost);
  const ssoId = useSelector(selectProfileSSOid);
  const token = useSelector(selectAuthAccessToken) ?? "";

  const { downloadInvoice } = useInvoiceDownloads({ intl, locale, token });
  const { limit, page, pageCount, setPage, skip } = usePagination(
    invoiceCount,
    INVOICES_PER_PAGE,
  );

  // Start on the newest year, and keep the guest's pick once they choose one.
  const [pickedYear, setPickedYear] = useState<string>();
  const selectedYear = pickedYear ?? latestYear;
  const [allCountries, setAllCountries] = useState<Record<string, Country>>({});

  useEffect(() => {
    (async () => {
      try {
        const countries = await new AddressFormatter(locale).getCountries();
        setAllCountries(
          reduce(
            countries,
            (countriesObj: Record<string, Country>, country) => {
              countriesObj[country.code] = country;
              return countriesObj;
            },
            {},
          ),
        );
      } catch {
        // Country names are cosmetic; leave them blank if the lookup fails.
      }
    })();
  }, [locale]);

  const formatDate = useCallback(
    (date: string) => {
      const momentDate = moment(date).locale(locale);
      return {
        month: momentDate.format("MMMM"),
        year: momentDate.format("YYYY"),
        day: momentDate.format("DD"),
      };
    },
    [locale],
  );

  useEffect(() => {
    if (selectedYear) {
      dispatch(
        fetchInvoicesByYear(ssoId ?? "", token, selectedYear, limit, skip),
      );
    }
  }, [dispatch, limit, selectedYear, skip, ssoId, token]);

  const handleYearChange = (invoiceYear: string) => {
    setPickedYear(invoiceYear);
    setPage(FIRST_PAGE);
  };

  if (invoiceYears.length === 0 || !token) {
    return null;
  }

  return (
    <>
      <div className="TaxInvoicesList tw-relative tw-flex tw-flex-col tw-gap-16">
        <Text element="h5" typeface="body-3" className="tw-m-0 ">
          <FormattedMessage
            id="hosting.tax_details_page.tax_invoices"
            defaultMessage="Tax invoices"
          />
        </Text>

        <Divider className="tw-m-0" />

        <Select value={selectedYear} onValueChange={handleYearChange}>
          <SelectTrigger
            className="tw-w-full sm:tw-w-[200px]"
            testId="invoice-year-select"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {map(invoiceYears, (year) => (
              <SelectItem key={year} value={String(year)}>
                {year}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {areInvoicesFetching ? (
          <div className="tw-flex tw-justify-center tw-py-16">
            <Spinner appearance="gray" size="large" />
          </div>
        ) : (
          <>
            {map(invoices, (invoice: Invoice) => {
              const {
                invoice_id,
                billing_country,
                fee_name,
                created,
                transactional,
                listing_title,
              } = invoice;
              const countryName = billing_country
                ? (allCountries[billing_country]?.name ?? "")
                : "";
              const { year, month, day } = formatDate(created ?? "");

              return (
                <InvoiceCard
                  key={invoice_id}
                  country={countryName}
                  invoiceId={invoice_id}
                  feeName={fee_name}
                  year={year}
                  month={month}
                  day={day}
                  isTransactional={transactional}
                  title={listing_title}
                  isHost={isHost}
                  onDownloadClick={() =>
                    downloadInvoice({
                      countryName,
                      day,
                      feeName: fee_name,
                      invoiceId: invoice_id,
                      isTransactional: transactional,
                      month,
                      year,
                    })
                  }
                />
              );
            })}
            {invoices && (
              <PaginationControls
                currentPage={page}
                totalPages={pageCount}
                onPageChange={(nextPage) => {
                  setPage(nextPage);
                }}
              />
            )}
          </>
        )}
      </div>
      <div id={PDF_CONTAINER_ID}>
        {/* PDF file renders in this container */}
      </div>
    </>
  );
};

export { TaxInvoicesList };
